import { LovedOneType } from './serviceRegistration';
import { ServiceRecipientFormValues } from './keyInInformation';

// Status of the logged-in user's own account, checked when the Welcome screen loads
export enum AccountStatus {
  // No account has been created for this user yet - onboarding has not been completed
  NotExisting = 'not_existing',
  // An account exists but has not been linked to the logged-in identity yet
  ExistingNotLinked = 'existing_not_linked',
  // An account exists and is already linked to the logged-in identity
  ExistingLinked = 'existing_linked',
  // An account exists but has not been migrated (migrationRequired=true from
  // /profile/migration-status) - the user must re-key their NRIC to run the same B3 lookup as
  // NotExisting before continuing to verify-account and the confirm step
  ExistingNotMigrated = 'existing_not_migrated'
}

// ----------------------------- API CONTRACT: myNH B3 Profile Linking (BU-20214) -----------------------------------

// GET /profile/migration-status - profile-service (BU-20810 / BU-20365)
export interface MigrationStatusResponse {
  // true when the user must walk the onboarding wizard before continuing
  migrationRequired: boolean;
  suggestedRole?: 'SELF' | null;
  // true when the user (SELF) or the currently-relevant NOK flow has fully completed onboarding.
  // Can be false even when migrationRequired=false (e.g. a linked NOK confirming a new loved one) -
  // createOnboardingSession is called whenever this is false, regardless of migrationRequired.
  completedOnboard: boolean;
  // Present only when migrationRequired = true. Bootstrap JWT minted by profile-service - used the
  // same way as appReducer's bootstrapToken (from POST /auth/validate) to authenticate the
  // /onboarding/sessions/* calls that follow for this (ExistingNotMigrated) branch.
  bootstrapToken?: string | null;
}

// POST /onboarding/sessions/{id}/role
export enum OnboardingRole {
  Self = 'SELF',
  Nok = 'NOK'
}

// POST /onboarding/sessions/{id}/lookup
export enum B3LookupResult {
  MatchLinked = 'MATCH_LINKED',
  MatchUnlinked = 'MATCH_UNLINKED',
  NoMatch = 'NO_MATCH'
}

export interface BlockedReason {
  reason: 'PROFILE_ALREADY_MANAGED';
  supportMailto?: string;
}

export interface LookupResponse {
  result: B3LookupResult;
  // Present only when result = MATCH_UNLINKED
  mobileLast4?: string | null;
  // Present only when role=NOK AND the B3 already has an active NOK_OF link
  blocked?: BlockedReason | null;
}

// POST /onboarding/sessions/{id}/otp/verify
export interface VerifyOtpRequest {
  // 6-digit OTP code
  code: string;
}

export interface VerifyOtpResponse {
  // "OTP_VERIFIED" on success
  status: OnboardingStatus;
  // Snapshot of the matched B3 client's details, used to prefill the confirm-details forms
  b3Snapshot?: B3ClientSnapshot;
  b3HasActivePackage?: boolean;
}

// Same shape as ServiceRecipientFormValues - the matched B3 client's details returned once OTP
// verification succeeds (SELF: the user's own existing record; NOK: the client's record). Used to
// prefill the confirm-details forms instead of the logged-in user's own profile.
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface B3ClientSnapshot extends ServiceRecipientFormValues {}

export enum OnboardingStatus {
  LinkpassAuthenticated = 'LINKPASS_AUTHENTICATED',
  RoleSelected = 'ROLE_SELECTED',
  NokClientLookup = 'NOK_CLIENT_LOOKUP',
  OtpPending = 'OTP_PENDING',
  OtpVerified = 'OTP_VERIFIED',
  OtpExhausted = 'OTP_EXHAUSTED',
  OtpExpired = 'OTP_EXPIRED',
  MobileUpdateRequested = 'MOBILE_UPDATE_REQUESTED',
  Completed = 'COMPLETED',
  Abandoned = 'ABANDONED'
}

// POST /onboarding/sessions/{id}/complete
// Field names mirror ServiceRecipientFormValues (fullName, homeNo, mobileNo, residentialStatus,
// languagesSpoken, isNtucMember, ntucMemberNo), not the User model's naming - dateOfBirth is sent as
// a plain YYYY-MM-DD date, not the form's ISO datetime string.

// SELF + NO_MATCH or NOK + NO_MATCH - creates a brand-new B3 client
export interface NewClientPayload {
  fullName: string;
  dateOfBirth: string;
  gender: string;
  nric: string;
  email: string;
  homeNo?: string;
  mobileNo: string;
  maritalStatus?: string;
  residentialStatus?: string;
  nationality?: string;
  languagesSpoken: string[];
  otherLanguage?: string;
  isNtucMember: boolean;
  ntucMemberNo?: string;
}

// SELF + MATCH_UNLINKED or NOK + MATCH_UNLINKED - audit/read-only, the B3 record already exists
export type ConfirmedClientPayload = Partial<NewClientPayload>;

// nokDetail on CompleteRequest - the NOK's own identity + relationship to the client. Required for
// role=NOK (both MATCH_UNLINKED and NO_MATCH); ignored for SELF. Replaces the retired
// PATCH /nok-details step - the FE now submits everything in one call to /complete.
export interface NokDetailPayload {
  name: string;
  email: string;
  mobile: string;
  relationshipToClient: string;
  relationshipOthers?: string;
  homeNo?: string;
  isDecisionMaker?: boolean;
}

export interface CompleteRequest {
  confirmedClient?: ConfirmedClientPayload;
  newClient?: NewClientPayload;
  nokDetail?: NokDetailPayload;
}

export interface CompleteResponse {
  // Full JWT, carries clientId + roles + privileges
  token: string;
  clientId: string;
  linkType?: 'SELF' | 'NOK_OF';
  status: OnboardingStatus;
}

// POST /onboarding/sessions/{id}/mobile-update-request (BU-20807 / BU-20218) - from the OTP screen,
// when the user can't receive the OTP. Terminates the session (MOBILE_UPDATE_REQUESTED is terminal).
export interface MobileUpdateRequestRequest {
  // 8-digit SG mobile, optionally with +65 prefix
  newMobile: string;
}

export interface MobileUpdateRequestResponse {
  sessionId: string;
  // Always "MOBILE_UPDATE_REQUESTED" for 202 responses
  status: OnboardingStatus;
  // Drives the FE's "N business days" confirmation copy
  estimatedBusinessDays: number;
}

// Read-only view of the NOK's own identity, returned on OnboardingSessionResponse.nokDetails for
// role=NOK sessions. name/email/mobile are sourced from the LinkPass SSO claims captured at session
// bootstrap (POST /onboarding/sessions), so these are populated as soon as the session exists -
// relationshipToClient/relationshipOthers/homeNo/isDecisionMaker are only ever submitted transiently
// at /complete (CompleteRequest.nokDetail) and are never persisted on the session, so they never
// come back here.
export interface NokDetailsResponse {
  name: string;
  email: string;
  mobile: string;
  relationshipToClient?: string;
  relationshipOthers?: string;
  homeNo?: string;
  isDecisionMaker?: boolean;
}

export interface LinkpassPrefill {
  email?: string;
  mobile?: string;
  name?: string;
  // [year, month, day] - month is 1-indexed (e.g. October = 10)
  dob?: [number, number, number];
  // Masked, e.g. "S****567A"; full NRIC never returned
  nric?: string;
}

// GET /onboarding/sessions/{id} (and returned by POST .../sessions, .../role, .../complete, ...)
export interface OnboardingSessionResponse {
  sessionId: string;
  status: OnboardingStatus;
  role: OnboardingRole | null;
  linkpassPrefill?: LinkpassPrefill;
  expiresAt: string;
  new: boolean;
  // Only present when status=OTP_PENDING
  attemptsRemaining?: number | null;
  b3LookupResult?: B3LookupResult | null;
  nokDetails?: NokDetailsResponse | null;
  b3HasActivePackage?: boolean;
}

export interface OnBoardingState {
  loading: boolean;
  // True from app start until checkAccountStatus has finished resolving accountStatus /
  // completedOnboard. Every guarded route renders blank while this is true, so a directly-hit URL
  // never flashes its page (or fires its own data calls) before we know whether onboarding is done -
  // see useRequiredOnboarding.
  isCheckingAccountStatus: boolean;
  lovedOne: LovedOneType | '';
  nric: string;
  // Status of the logged-in user's own account - fetched on Welcome screen mount
  accountStatus?: AccountStatus;
  // From /profile/migration-status - defaults the Welcome screen's role selection to SELF without
  // requiring the user to pick it manually
  suggestedRole?: 'SELF' | null;
  // Whether the verify-account (OTP) step has been completed
  isVerified: boolean;
  // OnboardingSession UUID from POST /onboarding/sessions - carried through every subsequent
  // /onboarding/sessions/{id}/* wizard call (role, lookup, otp, complete, ...)
  sessionId?: string;
  // Result of the most recent /onboarding/sessions/{id}/lookup call
  b3LookupResult?: B3LookupResult;
  // From the most recent /onboarding/sessions/{id}/lookup call (present when result =
  // MATCH_UNLINKED) - used by VerifyAccount to mask the OTP destination for the NRIC-lookup flow
  // (NotExisting / ExistingNotMigrated), instead of the logged-in user's own profile
  mobileLast4?: string | null;
  // Set only when a NOK's lookup hits a B3 client already claimed by another NOK
  lookupBlocked?: BlockedReason | null;
  // From /profile/migration-status - whether onboarding is fully done for the current flow. Used on
  // Welcome's NOK branch: true -> MyService, false -> ConfirmDetailsNOK
  completedOnboard: boolean;
  // From the most recent /onboarding/sessions/{id}/otp/verify call - prefills the confirm-details
  // forms (ConfirmDetailsMySelf / ConfirmDetailsNOK) in place of the logged-in user's own profile
  b3Snapshot?: B3ClientSnapshot;
  // Returned when verifying OTP - if true, FullName, Email, PhoneNumber are disabled in confirmation flow
  b3HasActivePackage?: boolean;
  // From the most recent /onboarding/sessions/{id}/role call - prefills KeyInInformation's
  // SelfDetailsForm / ServiceRecipientForm with the LinkPass identity data
  linkpassPrefill?: LinkpassPrefill;
  // Client-only marker set once Welcome hands off to the next wizard step - unlike sessionId (which
  // App.container's checkAccountStatus recreates on every app load regardless of route), this is
  // never set outside of Welcome, so its absence reliably means "this step wasn't reached through
  // the wizard" (e.g. a page refresh, which resets all onBoardingReducer state back to initial).
  // Used by useOnboardingSessionGuard to bounce every other onboarding step back to Welcome.
  localSessionId?: string;
}
